import { Box, Divider } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Button from 'components/Button';
import { FormikInput, FormikTextArea, FormikSelectAsyncPaginate, FormikCheckbox } from 'components/Formik';
import Title from 'components/Title';
import { routePaths } from 'global/routePaths';
import { fetchPaginatedJobs } from 'hooks/jobs/useFetchPaginatedJobs';
import { useSaveTask } from 'hooks/task/useSaveTask';
import { getAsyncSelectLoadOptions } from 'utils/helpers';

import { initialTaskFormData, taskFormValidationSchema } from './schema';

interface ITaskForm {
  formData?: typeof initialTaskFormData;
}

const TaskForm: React.FC<ITaskForm> = ({ formData = initialTaskFormData }) => {
  const { t } = useTranslation(['jobsTasks', 'common']);
  const navigate = useNavigate();

  const { status, mutate } = useSaveTask();

  useEffect(() => {
    if (status === 'success') {
      navigate(routePaths.jobsAndTasks.tasks);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const jobLoadOptions = async (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchPaginatedJobs, { search, page, job_status: 1 });
  };

  const handleFormSubmit = async (values: typeof initialTaskFormData) => {
    const postData = {
      ...values,
      billable: values.billable[0] === '1',
      jobs: Array.isArray(values.jobs) && values.jobs.length ? values.jobs.map((item: any) => item.id) : [],
    };
    mutate(postData);
  };

  return (
    <Box>
      <Title>{formData.id ? t('jobsTasks:updateTask') : t('jobsTasks:addTask')}</Title>
      <Divider mt="6" />
      <Box mt="10" maxWidth="500px" mx="auto">
        <Formik
          initialValues={formData}
          validationSchema={taskFormValidationSchema}
          enableReinitialize
          onSubmit={handleFormSubmit}
        >
          {({ values }) => (
            <Form noValidate>
              <FormikInput
                type="text"
                name="name"
                label={t('jobsTasks:taskName')}
                formControlProps={{ isRequired: true }}
              />
              <FormikCheckbox options={[{ label: t('jobsTasks:billable'), value: '1' }]} name="billable" />
              {values.billable.length > 0 && (
                <FormikInput
                  type="number"
                  name="billable_rate"
                  label={'Billable rate'}
                  formControlProps={{ isRequired: true }}
                />
              )}
              <FormikTextArea name="description" label={t('jobsTasks:taskDescription')} />
              <FormikSelectAsyncPaginate
                name="jobs"
                label={t('jobsTasks:jobs')}
                loadOptions={jobLoadOptions}
                selectProps={{
                  isMulti: true,
                  placeholder: t('jobsTasks:selectJobs'),
                  getOptionLabel: (option: any) => option.name,
                  getOptionValue: (option: any) => option.id,
                }}
              />
              <Button type="submit" colorScheme="secondary" isLoading={status === 'loading'}>
                {formData.id ? t('jobsTasks:updateTask') : t('jobsTasks:addTask')}
              </Button>
              <Button
                bg="gray.600"
                _hover={{ bg: 'gray.700' }}
                _active={{ bg: 'gray.700' }}
                ml="5"
                onClick={() => navigate(-1)}
              >
                {t('common:cancel')}
              </Button>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

export default TaskForm;
